/**
 * Attachments — Gemini-style image intake for the composer.
 *
 * useImageAttachments: file → client-side resize (1600px JPEG) → data URL.
 * AttachmentStrip: rounded thumbnails with hover-remove, shimmer while loading.
 * DropOverlay: page-wide drop target highlight.
 * Lightbox: full-screen viewer, Esc / click to close.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { resizeImage } from '../../chatbot/utils/imageUtils';
import { COLOR, FONT, G } from '../styles/tokens';

export const MAX_ATTACHMENTS = 6;

export interface Attachment {
  id:      string;
  dataUrl: string | null;
  name:    string;
  status:  'loading' | 'ready' | 'error';
}

export function imageFiles(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  return Array.from(dt.files).filter(f => f.type.startsWith('image/'));
}

export function useImageAttachments() {
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const addFiles = useCallback((files: File[]) => {
    const accepted = files.filter(f => f.type.startsWith('image/'));
    if (!accepted.length) return;
    setAttachments(prev => {
      const room = MAX_ATTACHMENTS - prev.length;
      const batch = accepted.slice(0, Math.max(0, room)).map(f => {
        const att: Attachment = { id: uuidv4(), dataUrl: null, name: f.name, status: 'loading' };
        resizeImage(f)
          .then(url => setAttachments(cur => cur.map(a => a.id === att.id ? { ...a, dataUrl: url, status: 'ready' as const } : a)))
          .catch(() => setAttachments(cur => cur.map(a => a.id === att.id ? { ...a, status: 'error' as const } : a)));
        return att;
      });
      return [...prev, ...batch];
    });
  }, []);

  const remove = useCallback((id: string) => setAttachments(prev => prev.filter(a => a.id !== id)), []);
  const clear  = useCallback(() => setAttachments([]), []);

  const ready   = attachments.filter(a => a.status === 'ready').map(a => a.dataUrl!);
  const loading = attachments.some(a => a.status === 'loading');

  return { attachments, addFiles, remove, clear, ready, loading };
}

// ── Thumbnail strip ───────────────────────────────────────────────────────────

export function AttachmentStrip({ attachments, onRemove }: {
  attachments: Attachment[];
  onRemove:    (id: string) => void;
}) {
  if (!attachments.length) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 8,
      padding: '2px 0 8px',
      animation: 'nora-fadein 0.15s ease',
    }}>
      {attachments.map(att => (
        <div
          key={att.id}
          className="nora-attachment"
          style={{
            position: 'relative', width: 64, height: 64,
            borderRadius: 10, overflow: 'hidden',
            border: `1px solid ${att.status === 'error' ? COLOR.red : COLOR.border}`,
            background: COLOR.bgOverlay,
            flexShrink: 0,
          }}
          title={att.name}
        >
          {att.status === 'ready' && att.dataUrl && (
            <img
              src={att.dataUrl}
              alt={att.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          {att.status === 'loading' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%',
                border: `2px solid ${COLOR.border}`,
                borderTopColor: COLOR.blue,
                animation: 'nora-spin 0.8s linear infinite',
              }} />
            </div>
          )}
          {att.status === 'error' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: COLOR.red, fontFamily: FONT.mono, fontSize: FONT.size.sm,
            }}>
              {G.warning}
            </div>
          )}
          <button
            onClick={() => onRemove(att.id)}
            className="nora-attachment-x"
            aria-label={`Remove ${att.name}`}
            style={{
              position: 'absolute', top: 3, right: 3,
              width: 18, height: 18, borderRadius: '50%',
              background: 'rgba(17,24,42,0.94)',
              border: `1px solid ${COLOR.border}`,
              color: COLOR.textPrimary,
              fontSize: 11, lineHeight: '15px', textAlign: 'center',
              cursor: 'pointer', padding: 0,
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Drop overlay ──────────────────────────────────────────────────────────────

export function DropOverlay({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div style={{
      position: 'absolute', inset: 8, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(11,16,32,0.94)',
      border: `2px dashed ${COLOR.blue}`,
      borderRadius: 12,
      pointerEvents: 'none',
      animation: 'nora-fadein 0.1s ease',
    }}>
      <div style={{ textAlign: 'center', fontFamily: FONT.sans }}>
        <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>🖼️</div>
        <div style={{ color: COLOR.textPrimary, fontSize: FONT.size.md, fontWeight: 600 }}>
          Drop images to attach
        </div>
        <div style={{ color: COLOR.textMuted, fontSize: FONT.size.sm, marginTop: 4 }}>
          PNG, JPEG, WebP · up to {MAX_ATTACHMENTS} images
        </div>
      </div>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

export function Lightbox({ src, onClose }: { src: string | null; onClose: () => void }) {
  useEffect(() => {
    if (!src) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [src, onClose]);

  if (!src) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.85)',
        cursor: 'zoom-out',
        animation: 'nora-fadein 0.12s ease',
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          maxWidth: '92vw', maxHeight: '88vh',
          borderRadius: 8,
          boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
        }}
      />
      <div style={{
        position: 'fixed', top: 16, right: 20,
        fontFamily: FONT.mono, fontSize: FONT.size.xs, color: COLOR.textMuted,
      }}>
        esc to close
      </div>
    </div>
  );
}
